import { httpService } from '../utils/http';
import {
  ApiResponse,
  AppCredentialConfigSaveRequest,
  MerchantConfigSaveRequest,
  Page,
  PaymentConfigAuditPageRequest,
  PaymentConfigAuditVO,
  PaymentChannelSaveRequest,
  PaymentConfigResponse,
  AppCredentialConfig,
  ClientPlatform,
  CredentialType,
  MerchantConfig,
  PlatformConfigQueryRequest,
  PlatformConfigSaveRequest,
  PlatformPaymentCapability,
  PlatformPaymentConfig,
  PlatformPaymentMethod,
  PlatformPaymentRoute,
  PlatformPaymentConfigQueryResponse,
  PaymentProvider,
} from '../types';
import {
  collectPlatformAppCredentials,
  collectPlatformMerchantConfigs,
  idsEqual,
  resolvePlatformSelectedRoute,
  toNumberId,
} from '../utils/paymentConfig';

interface BackendPaymentCapability {
  clientPlatform: ClientPlatform;
  paymentMethod: PlatformPaymentMethod;
  providers: PaymentProvider[];
  payActionType?: PlatformPaymentCapability['payActionType'];
}

interface BackendAppCredential {
  id?: number;
  appCredentialId?: number;
  provider?: PaymentProvider;
  paymentProvider?: PaymentProvider;
  clientPlatform?: ClientPlatform | null;
  credentialType: CredentialType;
  displayName?: string | null;
  appName?: string | null;
  appId?: string | null;
  enabled?: boolean;
  configComplete?: boolean;
  configParams?: Record<string, unknown> | null;
  sensitiveMask?: Record<string, unknown> | null;
}

interface BackendMerchantConfig {
  id?: number;
  merchantConfigId?: number;
  provider?: PaymentProvider;
  paymentProvider?: PaymentProvider;
  displayName?: string | null;
  name?: string | null;
  merchantNo?: string | null;
  sellerId?: string | null;
  hasAppAuthToken?: boolean;
  enabled?: boolean;
  configComplete?: boolean;
  configParams?: Record<string, unknown> | null;
  sensitiveMask?: Record<string, unknown> | null;
  gatewayAppId?: string | null;
  effectiveGatewayAppId?: string | null;
}

type BackendRoute = Omit<PlatformPaymentRoute, 'routeId'> & {
  id?: number;
  routeId?: number;
  routeScopeKey?: string | null;
  routeVersion?: number | null;
  activeScopeKey?: string | null;
};

interface BackendFlatPaymentConfigResponse {
  capabilities?: BackendPaymentCapability[];
  routes?: BackendRoute[];
  appCredentials?: BackendAppCredential[];
  merchantConfigs?: BackendMerchantConfig[];
}

const normalizeRoute = (route: BackendRoute): PlatformPaymentRoute => {
  const normalized: PlatformPaymentRoute = {
    ...route,
    routeId: toNumberId(route.routeId ?? route.id),
    clientPlatform: route.clientPlatform,
    paymentMethod: route.paymentMethod,
    paymentProvider: route.paymentProvider,
    enabled: route.enabled,
  };
  const appCredentialId = toNumberId(route.appCredentialId);
  const merchantConfigId = toNumberId(route.merchantConfigId);
  if (appCredentialId !== undefined) {
    normalized.appCredentialId = appCredentialId;
  }
  if (merchantConfigId !== undefined) {
    normalized.merchantConfigId = merchantConfigId;
  }
  const routeScopeKey = route.routeScopeKey?.trim();
  if (routeScopeKey) {
    normalized.routeScopeKey = routeScopeKey;
  }
  const routeVersion = toNumberId(route.routeVersion);
  if (routeVersion !== undefined) {
    normalized.routeVersion = routeVersion;
  }
  return normalized;
};

const inferCredentialPlatform = (
  credential: BackendAppCredential,
  routes: BackendRoute[],
): ClientPlatform => {
  if (credential.clientPlatform) {
    return credential.clientPlatform;
  }
  const credentialId = toNumberId(credential.id ?? credential.appCredentialId);
  const route = routes.find(item => idsEqual(item.appCredentialId, credentialId));
  if (route) {
    return route.clientPlatform;
  }
  if (credential.credentialType === 'WECHAT_MP') {
    return 'WECHAT_H5';
  }
  if (credential.credentialType === 'WECHAT_MINIAPP') {
    return 'WEAPP';
  }
  return 'ALIPAY';
};

const readConfigParam = (
  params: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined => {
  const raw = params?.[key];
  if (typeof raw !== 'string') {
    return undefined;
  }
  const trimmed = raw.trim();
  return trimmed || undefined;
};

const hasSensitiveMaskKey = (
  mask: Record<string, unknown> | null | undefined,
  key: string,
): boolean => {
  if (!mask || !(key in mask)) {
    return false;
  }
  const value = mask[key];
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value != null;
};

const normalizeAppCredential = (credential: BackendAppCredential, routes: BackendRoute[]): AppCredentialConfig => {
  const appCredentialId = toNumberId(credential.id ?? credential.appCredentialId) as number;
  const appId = credential.appId?.trim() || '';
  const appName =
    (credential.displayName ?? credential.appName ?? '').trim() || `应用资质 ${appCredentialId}`;
  const gatewayAppId = readConfigParam(credential.configParams, 'gatewayAppId');

  return {
    appCredentialId,
    paymentProvider: credential.provider ?? credential.paymentProvider ?? undefined,
    clientPlatform: inferCredentialPlatform(credential, routes),
    appId,
    appName,
    credentialType: credential.credentialType,
    gatewayAppId,
    configComplete: credential.configComplete ?? false,
    sensitiveStatus: {
      summary: credential.sensitiveMask ? Object.keys(credential.sensitiveMask).join('、') : undefined,
    },
  };
};

const normalizeMerchantConfig = (config: BackendMerchantConfig): MerchantConfig => {
  const merchantConfigId = toNumberId(config.id ?? config.merchantConfigId) as number;
  const name = (config.displayName ?? config.name ?? '').trim() || `商户/收款账号 ${merchantConfigId}`;
  const privateKeyConfigured =
    hasSensitiveMaskKey(config.sensitiveMask, 'privateKey') ||
    hasSensitiveMaskKey(config.sensitiveMask, 'merchantPrivateKey');
  const wechatPayPublicKeyId = readConfigParam(config.configParams, 'wechatPayPublicKeyId');
  const wechatPayPublicKeyMask = readConfigParam(config.sensitiveMask, 'wechatPayPublicKey');
  const wechatPayPublicKeyConfigured = hasSensitiveMaskKey(config.sensitiveMask, 'wechatPayPublicKey');
  const gatewayAppId = config.gatewayAppId?.trim() || readConfigParam(config.configParams, 'gatewayAppId');
  const effectiveGatewayAppId = config.effectiveGatewayAppId?.trim() || undefined;

  return {
    merchantConfigId,
    paymentProvider: config.provider ?? config.paymentProvider ?? 'WECHAT_OFFICIAL',
    merchantNo: config.merchantNo || undefined,
    sellerId: config.sellerId || undefined,
    appAuthTokenConfigured: config.hasAppAuthToken,
    privateKeyConfigured,
    wechatPayPublicKeyId,
    wechatPayPublicKeyConfigured,
    wechatPayPublicKeyMask,
    gatewayAppId,
    effectiveGatewayAppId,
    name,
    configComplete: config.configComplete ?? false,
    sensitiveStatus: {
      privateKeyConfigured,
      summary: config.sensitiveMask ? Object.keys(config.sensitiveMask).join('、') : undefined,
    },
  };
};

const normalizePlatformConfig = (platform: PlatformPaymentConfig): PlatformPaymentConfig => {
  const candidateRoutes = (platform.candidateRoutes || []).map(route => normalizeRoute(route as BackendRoute));
  return {
    ...platform,
    enabledRouteId: toNumberId(platform.enabledRouteId),
    candidateRoutes,
    appCredentialConfigs: (platform.appCredentialConfigs || []).map(item =>
      normalizeAppCredential(item as BackendAppCredential, candidateRoutes as BackendRoute[]),
    ),
    merchantConfigs: (platform.merchantConfigs || []).map(item => normalizeMerchantConfig(item as BackendMerchantConfig)),
  };
};

const normalizePaymentConfigResponse = (
  data?: PlatformPaymentConfigQueryResponse | BackendFlatPaymentConfigResponse,
): PlatformPaymentConfigQueryResponse | undefined => {
  if (!data) {
    return undefined;
  }

  if ('platforms' in data && !('capabilities' in data) && !('appCredentials' in data)) {
    const platforms = (data.platforms || []).map(normalizePlatformConfig);
    return {
      platforms,
      allAppCredentialConfigs: platforms.flatMap(platform => platform.appCredentialConfigs || []),
      allMerchantConfigs: platforms.flatMap(platform => platform.merchantConfigs || []),
    };
  }

  const flatData = data as BackendFlatPaymentConfigResponse;
  const capabilities = flatData.capabilities || [];
  const routes = flatData.routes || [];
  const appCredentialConfigs = (flatData.appCredentials || []).map(item => normalizeAppCredential(item, routes));
  const merchantConfigs = (flatData.merchantConfigs || []).map(normalizeMerchantConfig);
  const platforms = capabilities.reduce<PlatformPaymentConfig[]>((result, capability) => {
    const existing = result.find(item => item.clientPlatform === capability.clientPlatform);
    const platformRoutes = routes.filter(route => route.clientPlatform === capability.clientPlatform);
    const paymentProviderSet = new Set<PaymentProvider>(capability.providers);
    const normalizedRoutes = platformRoutes.map(normalizeRoute);
    const platformAppCredentialConfigs = collectPlatformAppCredentials(
      capability.clientPlatform,
      appCredentialConfigs,
      paymentProviderSet,
    );
    const platformMerchantConfigs = collectPlatformMerchantConfigs(merchantConfigs, paymentProviderSet);
    const selectedRoute = resolvePlatformSelectedRoute(normalizedRoutes, {
      appCredentials: appCredentialConfigs,
      merchants: merchantConfigs,
    });
    const next: PlatformPaymentConfig = existing || {
      clientPlatform: capability.clientPlatform,
      supportedMethods: [],
      capabilities: [],
      candidateRoutes: normalizedRoutes,
      enabledRouteId: selectedRoute?.routeId,
      supportedProviders: capability.providers,
      appCredentialConfigs: platformAppCredentialConfigs,
      merchantConfigs: platformMerchantConfigs,
    };

    if (!next.supportedMethods.includes(capability.paymentMethod)) {
      next.supportedMethods.push(capability.paymentMethod);
    }
    next.supportedProviders = Array.from(new Set([...(next.supportedProviders || []), ...capability.providers]));
    const nextCapabilities = next.capabilities || [];
    if (!nextCapabilities.some(item => item.paymentMethod === capability.paymentMethod)) {
      nextCapabilities.push({
        clientPlatform: capability.clientPlatform,
        paymentMethod: capability.paymentMethod,
        providers: capability.providers,
        payActionType: capability.payActionType,
      });
      next.capabilities = nextCapabilities;
    }
    if (!existing) {
      result.push(next);
    }
    return result;
  }, []);

  return {
    platforms,
    allAppCredentialConfigs: appCredentialConfigs,
    allMerchantConfigs: merchantConfigs,
  };
};

class PaymentConfigService {
  // 兼容旧版全局支付通道配置
  queryConfig(): Promise<ApiResponse<PaymentConfigResponse>> {
    return httpService.post('/payment/config/query');
  }

  // 兼容旧版全局支付通道配置
  saveChannel(params: PaymentChannelSaveRequest): Promise<ApiResponse<null>> {
    return httpService.post('/payment/config/channel/save', params);
  }

  queryPlatformConfig(
    params: PlatformConfigQueryRequest = {},
  ): Promise<ApiResponse<PlatformPaymentConfigQueryResponse>> {
    return httpService.post('/payment/config/platform/query', params).then(res => ({
      ...res,
      data: normalizePaymentConfigResponse(res.data),
    }));
  }

  queryPaymentConfig(
    params: PlatformConfigQueryRequest = {},
  ): Promise<ApiResponse<PlatformPaymentConfigQueryResponse>> {
    return this.queryPlatformConfig(params);
  }

  /** 成功时 data 为不阻断保存的提示信息，需展示给操作人。 */
  savePlatformConfig(params: PlatformConfigSaveRequest): Promise<ApiResponse<string[] | null>> {
    return httpService.post('/payment/config/platform/save', params);
  }

  saveAppCredentialConfigs(
    appCredentialConfigs: AppCredentialConfigSaveRequest[],
  ): Promise<ApiResponse<string[] | null>> {
    return this.savePlatformConfig({ appCredentialConfigs });
  }

  saveMerchantConfigs(merchantConfigs: MerchantConfigSaveRequest[]): Promise<ApiResponse<string[] | null>> {
    return this.savePlatformConfig({ merchantConfigs });
  }

  saveRoutes(routes: PlatformPaymentRoute[]): Promise<ApiResponse<string[] | null>> {
    return this.savePlatformConfig({ routes });
  }

  queryAuditPage(params: PaymentConfigAuditPageRequest): Promise<ApiResponse<Page<PaymentConfigAuditVO>>> {
    return httpService.post('/payment/config/audit/query', params);
  }
}

export const paymentConfigService = new PaymentConfigService();
